import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { authFetch, publicFetch } from '@/services/api'; // adjust path if needed

export type CartItem = {
  itemId?: number;
  variantId: number;
  productId: number;
  productName: string;
  salePrice: number;
  originalPrice?: number;
  offerPrice?: number;
  quantity: number;
  size: string;
  sizeName?: string;
  color: string;
  sku?: string;
  stockQuantity?: number;
  images: {
    id: number;
    imageUrl: string;
    imageType: string;
    displayOrder: number;
  }[];
  productCategoryName?: string | null;
  productSubCategoryName?: string | null;
  vendor?: { vendorId: number; vendorName: string };
  seller: {
    id: number | null;
    shopName: string;
    city: string;
    state: string;
  };
};

type CartContextType = {
  cart: CartItem[];
  total: number;
  totalItems: number;
  addToCart: (item: CartItem) => Promise<void>;
  updateQuantity: (variantId: number, qty: number) => Promise<void>;
  removeFromCart: (itemId?: number, variantId?: number) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
  isInCart: (variantId: number) => boolean;
};

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth(); // Get user for authentication state
  const [cart, setCart] = useState<CartItem[]>([]);
  const [total, setTotal] = useState(0);

  const fetchProductImages = useCallback(async (productId: number) => {
    try {
      const resp = await publicFetch(`/api/products/${productId}`);
      if (!resp.ok) return [];
      const data = await resp.json();
      const images = data.images || data.variants?.[0]?.images || [];
      return images.map((img: any) => ({
        id: img.id || Date.now(),
        imageUrl: img.imageUrl || img.image_url || '/placeholder-product.png',
        imageType: img.imageType || 'OTHER',
        displayOrder: img.displayOrder || 999,
      }));
    } catch {
      return [];
    }
  }, []);

  const fetchCart = useCallback(async () => {
    try {
      let resp: Response;

      if (user.isAuthenticated && user?.customerId) {
        try {
          resp = await authFetch('/api/cart');
        } catch (authError) {
          console.log('fetchCart auth error, falling back to guest mode:', authError);
          // If auth fails, try to create a guest session and use that
          let guestId = localStorage.getItem('guestSessionId');
          if (!guestId) {
            guestId = 'guest-' + Math.random().toString(36).substring(2, 10);
            localStorage.setItem('guestSessionId', guestId);
          }
          try {
            resp = await publicFetch(`/api/cart?guestSessionId=${guestId}`);
          } catch (guestError) {
            console.log('Guest cart fetch also failed:', guestError);
            setCart([]);
            return;
          }
        }
      } else if (user?.guestSessionId) {
        resp = await publicFetch(`/api/cart?guestSessionId=${user.guestSessionId}`);
      } else {
        setCart([]);
        return;
      }

      if (!resp.ok) {
        if (resp.status === 404 || resp.status === 500) {
          // Cart doesn't exist or server error - start with empty cart
          console.log(`Cart not found or server error (${resp.status}), starting with empty cart`);
          setCart([]);
        }
        return;
      }

      const data = await resp.json();
      const items = data.items || [];

      const mapped: CartItem[] = await Promise.all(
        items.map(async (item: any) => {
          let images = (item.images || []).map((img: any) => ({
            id: img.id || Date.now(),
            imageUrl: img.imageUrl || img.image_url || '/placeholder-product.png',
            imageType: img.imageType || 'OTHER',
            displayOrder: img.displayOrder || 999,
          }));

          if (images.length === 0 && item.productId) {
            images = await fetchProductImages(item.productId);
          }

          return {
            itemId: item.itemId,
            variantId: item.variantId,
            productId: item.productId,
            productName: item.productName || 'Unknown Product',
            salePrice: Number(item.offerPrice ?? item.salePrice ?? item.originalPrice ?? 0),
            originalPrice: item.originalPrice ? Number(item.originalPrice) : undefined,
            offerPrice: item.offerPrice ? Number(item.offerPrice) : undefined,
            quantity: item.quantity || 1,
            size: item.sizeCode || 'N/A',
            sizeName: item.sizeName,
            color: item.color || 'N/A',
            sku: item.sku,
            stockQuantity: item.stockQuantity || 0,
            productCategoryName: item.productCategoryName || null,
            productSubCategoryName: item.productSubCategoryName || null,
            images,
            vendor: item.vendorId
              ? { vendorId: item.vendorId, vendorName: item.vendorName || '' }
              : undefined,
            seller: {
              id: item.sellerId || null,
              shopName: item.sellerName || 'Unknown Shop',
              city: item.sellerCity || 'Chennai',
              state: item.sellerState || 'Tamil Nadu',
            },
          };
        })
      );

      setCart(mapped);
    } catch (e) {
      console.warn('fetchCart error (this is expected for new users):', e);
      // For new users, cart API might fail initially, so start with empty cart
      setCart([]);
    }
  }, [user.isAuthenticated, user?.customerId, user?.guestSessionId, fetchProductImages]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  useEffect(() => {
    const handler = () => {
      console.log('CartContext: refresh event received');
      fetchCart();
    };

    window.addEventListener('cart:refresh', handler);
    return () => window.removeEventListener('cart:refresh', handler);
  }, [fetchCart]);


  useEffect(() => {
    setTotal(cart.reduce((s, i) => s + i.salePrice * i.quantity, 0));
  }, [cart]);

  const saveCart = async (items: CartItem[]): Promise<CartItem[]> => {
    if (!user.isAuthenticated && !user?.guestSessionId) {
      // Store in localStorage for guest users without session
      localStorage.setItem('cart', JSON.stringify(items));
      return items;
    }

    const payload = items.map(i => ({ variantId: i.variantId, quantity: i.quantity }));

    try {
      console.log('saveCart: Saving cart to backend:', payload);

      let response;

      if (user.isAuthenticated) {
        try {
          response = await authFetch('/api/cart', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cartItems: payload }),
          });
        } catch (authError) {
          console.log('saveCart auth failed, trying guest mode:', authError);
          // Fallback to guest mode
          let guestId = user.guestSessionId || localStorage.getItem('guestSessionId');
          if (!guestId) {
            guestId = 'guest-' + Math.random().toString(36).substring(2, 10);
            localStorage.setItem('guestSessionId', guestId);
          }
          response = await publicFetch('/api/cart', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ guestSessionId: guestId, cartItems: payload }),
          });
        }
      } else {
        response = await publicFetch('/api/cart', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ guestSessionId: user.guestSessionId, cartItems: payload }),
        });
      }

      if (!response.ok) {
        console.error('saveCart failed with status:', response.status);
        return items; // fallback to local state
      }

      const data = await response.json();
      const backendItems = data.items || [];

      // Map exactly like fetchCart does to ensure full data
      const mapped: CartItem[] = await Promise.all(
        backendItems.map(async (item: any) => {
          let images = (item.images || []).map((img: any) => ({
            id: img.id || Date.now(),
            imageUrl: img.imageUrl || img.image_url || '/placeholder-product.png',
            imageType: img.imageType || 'OTHER',
            displayOrder: img.displayOrder || 999,
          }));

          if (images.length === 0 && item.productId) {
            images = await fetchProductImages(item.productId);
          }

          return {
            itemId: item.itemId,
            variantId: item.variantId,
            productId: item.productId,
            productName: item.productName || 'Unknown Product',
            salePrice: Number(item.offerPrice ?? item.salePrice ?? item.originalPrice ?? 0),
            originalPrice: item.originalPrice ? Number(item.originalPrice) : undefined,
            offerPrice: item.offerPrice ? Number(item.offerPrice) : undefined,
            quantity: item.quantity || 1,
            size: item.sizeCode || 'N/A',
            sizeName: item.sizeName,
            color: item.color || 'N/A',
            sku: item.sku,
            stockQuantity: item.stockQuantity || 0,
            productCategoryName: item.productCategoryName || null,
            productSubCategoryName: item.productSubCategoryName || null,
            images,
            vendor: item.vendorId
              ? { vendorId: item.vendorId, vendorName: item.vendorName || '' }
              : undefined,
            seller: {
              id: item.sellerId || null,
              shopName: item.sellerName || 'Unknown Shop',
              city: item.sellerCity || 'Chennai',
              state: item.sellerState || 'Tamil Nadu',
            },
          };
        })
      );

      return mapped;
    } catch (e) {
      console.error('saveCart exception:', e);
      return items; // fallback
    }
  };

  const addToCart = async (item: CartItem) => {
  console.log('Adding item to cart:', item);

  const existing = cart.find(i => i.variantId === item.variantId);

  const optimisticCart = existing
    ? cart.map(i =>
        i.variantId === item.variantId
          ? {
              ...i,
              quantity: Math.min(
                i.quantity + item.quantity, // ✅ USE PDP QUANTITY
                i.stockQuantity ?? Infinity
              ),
            }
          : i
      )
    : [...cart, { ...item }]; // ✅ KEEP PASSED QUANTITY

  setCart(optimisticCart); // instant UI update

  try {
    const syncedCart = await saveCart(optimisticCart);
    setCart(syncedCart);
  } catch (error) {
    console.error('Failed to sync cart:', error);
  }
};

  const updateQuantity = async (variantId: number, qty: number) => {
    if (qty < 1) {
      removeFromCart(undefined, variantId);
      return;
    }
    const optimistic = cart.map(i => (i.variantId === variantId ? { ...i, quantity: qty } : i));
    setCart(optimistic);
    const synced = await saveCart(optimistic);
    setCart(synced);
  };

  const removeFromCart = async (itemId?: number, variantId?: number) => {
    let newCart = cart;
    if (itemId) {
      newCart = cart.filter(i => i.itemId !== itemId);
    } else if (variantId) {
      newCart = cart.filter(i => i.variantId !== variantId);
    }
    setCart(newCart);
    const synced = await saveCart(newCart);
    setCart(synced);
  };

  const clearCart = async () => {
    setCart([]);
    await saveCart([]);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        total,
        totalItems: cart.reduce((sum, item) => sum + item.quantity, 0),
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        refreshCart: fetchCart,
        isInCart: (variantId) => cart.some(i => i.variantId === variantId),
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};